<template>
<div class='widget-W0010V0001'>
  <div class="widget">
    <h2 class="widget-title">Seneste forløb</h2>
    <div v-if="loading" class="widget-loader">
      <i class="fa fa-spinner"></i>
      Loading...
    </div>
    <div v-if="hasErrors">
      <ul class="errors">
        <li v-for="error in errors" v-bind:key="error" class="errors__error">{{ error }}</li>
      </ul>
    </div>
    <div v-if="!loading">
      <ul v-if="items && items.length" class="widget">
        <li v-for="item in items" v-bind:key="item.id" class="widget__item">

          <div v-if="item.books && item.books.length > 0">
            <ul class="widgetlist">
              <li
                v-for="book in item.books.slice(0, maxShown)"
                v-bind:key="book.id"
                class="widgetlist__item"
                :style="getBookIcon()"
              >
                <form
                  :action="getApiBaseUrl() + 'teacherlasteditedbooklinkauth'"
                  method="GET"
                  target="_blank"
                  class="widgetlist__item__form"
                >
                  <input type="hidden" name="bookId" :value="book.id"/>
                  <input type="hidden" name="sessionUUID" :value="sessionUUID"/>
                  <input type="hidden" name="aulaJWT" :value="aulaToken"/>
                  <input type="hidden" name="currentWeekNumber" :value="getThisYearWeek()"/>
                  <button
                    type="submit"
                    class="widgetlist__item__link"
                    :title="`Se ${book.title} (${book.category}) i Meebook`"
                  >
                    <div class="widgetlist__item__title">
                      <span>{{ book.title }}</span>
                    </div>
                    <div class="widgetlist__item__subtitle">
                      <span>{{ book.category }}</span>
                    </div>
                  </button>
                </form>
              </li>
            </ul>
            <button
              v-if="item.books.length > 3"
              class="widgetlist__showall"
              v-on:click="toggleShowAll"
            >
              {{ showAll ? 'Se færre' : 'Se alle' }}
            </button>
          </div>
          <div v-else>
            <p>Ingen forløb at vise</p>
          </div>
        </li>
      </ul>
      <div v-else-if="!hasErrors">
        <p>Ingen forløb at vise</p>
      </div>
    </div>
  </div>
</div>
</template>

<script>
var aulaWidgetMixin = {
  props: {
    axios: Function,
    moment: Function,
    getAulaToken: Function,
    devServer: String,
    sessionUUID: String,
    placement: String,
    currentWeekNumber: String,
    isMobileApp: Boolean,
    assuranceLevel: Number,
    userProfile: String,
    childFilter: [String, Array],
    institutionFilter: [String, Array],
    group: String,
    meebookImgBaseUrl: {
      type: String,
      default: 'https://app.meebook.com/images/aula/',
    },
    meebookApiBaseUrl: String,
  },
  data() {
    return {
      loading: true,
      items: [],
      errors: [],
      showAll: false,
      aulaToken: null,
    };
  },
  mounted() {
    this.aulaToken = this.getAulaToken();

// AULA JWTs live ~5 min. Keep ours fresh in the background so the
// hidden form inputs (which embed the token at render time) are
// always valid when the user clicks. Vue's reactivity updates the
// inputs automatically when this.aulaToken changes.
//
// NOTE: AULA support has explicitly disallowed addEventListener()
// on document or window in widgets, so we rely on setInterval alone.
    this._aulaTokenRefreshInterval = setInterval(() => {
      this.refreshAulaTokenSilently();
    }, 4 * 60 * 1000);
  },
  beforeDestroy() {
    if (this._aulaTokenRefreshInterval) {
      clearInterval(this._aulaTokenRefreshInterval);
    }
  },
  watch: {
    aulaToken: function (newVal, oldVal) {
// Only trigger loadContent on the initial token arrival (empty -> value).
// Subsequent token changes (from ensureAulaToken during 401 retry, paging,
// or time-based refresh) must NOT re-trigger loadContent — the widget's
// own isRetry logic handles reloads and caps them at 1 retry.
      if (newVal && !oldVal) {
        this.loadContent();
      }
    },
  },
  computed: {
    maxShown() {
      return this.showAll ? Infinity : 3;
    },
    hasErrors() {
      return this.errors && this.errors.length;
    },
  },
  methods: {
    async ensureAulaToken() {
      const fresh = await this.getAulaToken();
// Don't clobber a valid token with null/undefined — AULA can briefly
// return empty values while a new token is being negotiated.
      if (fresh) {
        this.aulaToken = fresh;
      }
      return this.aulaToken;
    },
    async refreshAulaTokenSilently() {
// Background refresh — never surface errors to the user. The 401
// retry path in loadContent() is still our backstop.
      try {
        const fresh = await this.getAulaToken();
        if (fresh) {
          this.aulaToken = fresh;
        }
      } catch (e) { /* swallow */
      }
    },
    async getClient() {
      await this.ensureAulaToken();
      return this.axios.create({
        baseURL: this.getApiBaseUrl(),
        timeout: 30000,
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          'Accept': 'application/json',
          'Authorization': 'Bearer ' + this.aulaToken,
          'sessionUUID': this.sessionUUID,
          'X-Version': '1.0',
        },
      });
    },
    getApiBaseUrl() {
      return "https://app.meebook.com/aulaapi/";
    },
    getBookIcon() {
      return 'background-image: url(' + this.meebookImgBaseUrl + 'meebook-2x.png)';
    },
    toggleShowAll() {
      this.showAll = !this.showAll;
    },
  },
};
export default {
  mixins: [aulaWidgetMixin],
  props: {
    widgetIdentifier: {
      type: String,
      default: 'teacherLastEditedBooks-',
    },
  },
  methods: {
    getThisYearWeek() {
      this.moment.locale('da');
      return this.moment().format(this.moment.HTML5_FMT.WEEK);
    },
    async loadContent(isRetry = false) {
      this.errors = [];

      try {
        let http = await this.getClient();

        const response = await http.get('teacherlasteditedbook/all', {
          params: {
            currentWeekNumber: this.getThisYearWeek(),
            institutionFilter: this.institutionFilter,
          }
        });

        this.loading = false;
        this.items = response.data || [];

      } catch (error) {
        // 401 EXPIRED TOKEN LOGIC
        if (error.response && error.response.status === 401 && !isRetry) {
          this.loading = true;
          this.errors.push('Din Aula-token fornyes.. Øjeblik');

          try {
            await this.ensureAulaToken();

            this.errors.pop();

            return this.loadContent(true);
          } catch (retryError) {
            this.errors.pop();
          }
        }

        // STANDARD ERROR LOGIC
        this.loading = false;
        this.items = [];
        if (error.response && error.response.data && error.response.data.exceptionMessage) {
          this.errors.push(error.response.data.exceptionMessage);
        } else {
          this.errors.push('Forløb kunne ikke hentes.');
        }
      }
    }
  },
}
</script>

<style lang="scss">
.widget-W0010V0001 {
.widget-loader {
  font-family: "Lato", "Avenir", Helvetica, Arial, sans-serif;
  font-size: 16px;
  margin: 20px 0
}

.widget-loader > .fa {
  animation-name: spin;
  animation-duration: 1s;
  animation-iteration-count: infinite;
  animation-timing-function: linear;
  animation-fill-mode: both;
  margin-right: 10px
}

@keyframes spin {
  0% {
    transform: rotate(0)
  }
  100% {
    transform: rotate(360deg)
  }
}

.errors {
  margin-top: 20px
}

.errors__error {
  display: block;
  padding: 15px 20px;
  background-color: #fff;
  color: #b50050
}

* {
  box-sizing: border-box
}

ul {
  margin: 0;
  padding: 0;
  list-style-type: none
}

li {
  margin: 0;
  width: 100%
}

.widget-title {
  display: block;
  text-align: left;
  font-family: "Lato", "Avenir", Helvetica, Arial, sans-serif;
  font-size: 17px;
  font-weight: 700;
  text-transform: uppercase;
  margin-bottom: 0;
  margin-top: 30px
}

.widget {
  text-align: left
}

.widget__item__name {
  display: block;
  font-size: 14px;
  font-weight: 700;
  text-transform: capitalize;
  color: #5d6c79;
  margin-top: 30px;
  margin-bottom: 10px
}

.widgetlist {
  text-align: left
}

.widgetlist__item {
  position: relative;
  padding: 15px 20px;
  background-color: #fff;
  margin-bottom: 5px;
  cursor: pointer;
  outline: 0
}

.widgetlist__item:focus {
  background-color: rgba(255, 255, 255, .4)
}

.widgetlist__item__title {
  font-size: 16px;
  font-weight: 700
}

.widgetlist__item__subtitle {
  font-size: 11px;
  font-weight: 700;
  color: #6a7681;
  text-transform: capitalize
}

.widgetlist__item__subtitle::first-letter {
  text-transform: uppercase
}

.widgetlist__item__form {
  margin: 0;
  padding: 0
}

.widgetlist__item__link {
  display: block;
  width: 100%;
  color: inherit;
  text-decoration: none;
  outline: none;
  background: rgba(0, 0, 0, 0);
  border: 0;
  padding: 0;
  margin: 0;
  text-align: left;
  font: inherit;
  cursor: pointer
}

.widgetlist__item__link:focus-visible {
  outline: 2px solid #f5836d;
  outline-offset: -2px
}

.widgetlist__showall {
  display: block;
  padding: 0;
  margin-top: 5px;
  font-size: 14px;
  font-weight: 700;
  text-decoration: underline;
  cursor: pointer;
  background-color: rgba(0, 0, 0, 0);
  border: 0;
  outline: none
}

.widgetlist__showall:focus {
  opacity: .5
}

.widget {
  margin-top: 20px
}

.widgetlist__item {
  padding: 15px 20px 15px 70px;
  background-repeat: no-repeat;
  background-position: -5px center;
  background-size: 70px
}
}
</style>
